import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Chip, 
  Button,
  Collapse
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { apiService } from '../services/api';
import { RegionsData } from '../types';

// Цветовая схема из онбординга
const FigmaColors = {
  background: '#FFFFFF',
  darkBg: '#d0e1f9',
  inputBg: 'rgb(248, 242, 255)', // Фон полей форм
  inputPlaceholder: '#000000',
  primaryButton: '#4d648d',
  primaryButtonGradient: 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)',
  chipBorder: '#4d648d',
  progressBg: '#d0e1f9',
  progressFill: '#4d648d',
  spacerDark: '#d0e1f9',
  headingColor: '#000000', // Цвет заголовков
};

interface RegionSelectorProps {
  selectedRegion: string | null;
  onRegionChange: (regionSlug: string) => void;
  error?: string;
}

const RegionSelector: React.FC<RegionSelectorProps> = ({ 
  selectedRegion, 
  onRegionChange, 
  error 
}) => {
  const [regionsData, setRegionsData] = useState<RegionsData | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const loadRegions = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        const data = await apiService.getRegions();
        setRegionsData(data);
      } catch (error) {
        console.error('Error loading regions:', error);
        setLoadError('Не удалось загрузить список регионов');
      } finally {
        setIsLoading(false);
      }
    };

    loadRegions();
  }, []);

  const handleRegionSelect = (regionSlug: string) => {
    onRegionChange(regionSlug);
  };

  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };


  if (isLoading) {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: FigmaColors.headingColor }}>
          ГЕО
        </Typography>
        <Typography variant="body2" sx={{ color: FigmaColors.inputPlaceholder }}>
          Загрузка регионов...
        </Typography>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: FigmaColors.headingColor }}>
          ГЕО
        </Typography>
        <Typography variant="body2" sx={{ color: '#f44336' }}>
          {loadError}
        </Typography>
      </Box>
    );
  }

  if (!regionsData) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: FigmaColors.headingColor }}>
        ГЕО
      </Typography>
      
      {/* Быстрые регионы */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {regionsData.quick_regions.map((region) => (
          <Chip
            key={region.slug}
            label={region.name}
            clickable
            variant={selectedRegion === region.slug ? "filled" : "outlined"}
            onClick={() => handleRegionSelect(region.slug)}
            sx={{
              borderColor: selectedRegion === region.slug ? '#000000' : '#ececfc',
              border: selectedRegion === region.slug ? '1px solid #000000' : '1px solid #ececfc',
              color: selectedRegion === region.slug ? '#ffffff' : '#514b81',
              bgcolor: selectedRegion === region.slug ? '#000000' : '#ffffff',
              '&:hover': {
                bgcolor: selectedRegion === region.slug ? '#000000' : '#ffffff',
                opacity: 0.8,
              },
            }}
          />
        ))}
      </Box>

      {/* Кнопка "Ещё регионы" */}
      <Button
        onClick={handleToggleExpanded}
        endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
        sx={{
          color: FigmaColors.inputPlaceholder,
          textTransform: 'none',
          fontWeight: 500,
          mb: 1,
          '&:hover': {
            bgcolor: 'transparent',
            opacity: 0.7,
          },
        }}
      >
        {isExpanded ? 'Скрыть регионы' : 'Ещё регионы'}
      </Button>

      {/* Раскрывающаяся секция со всеми регионами */}
      <Collapse in={isExpanded}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {regionsData.all_regions.map((region) => (
            <Chip
              key={region.slug}
              label={region.name}
              clickable
              variant={selectedRegion === region.slug ? "filled" : "outlined"}
              onClick={() => handleRegionSelect(region.slug)}
              sx={{
                borderColor: selectedRegion === region.slug ? '#000000' : '#ececfc',
                border: selectedRegion === region.slug ? '1px solid #000000' : '1px solid #ececfc',
                color: selectedRegion === region.slug ? '#ffffff' : '#514b81',
                bgcolor: selectedRegion === region.slug ? '#000000' : '#ffffff',
                '&:hover': {
                  bgcolor: selectedRegion === region.slug ? '#000000' : '#ffffff',
                  opacity: 0.8,
                },
              }}
            />
          ))}
        </Box>
      </Collapse>

      {/* Ошибка валидации */}
      {error && (
        <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default RegionSelector;
